/**
 * Central filter apply pipeline.
 * Applies filter state to data in a deterministic order. Used by ScopeTable so filter logic lives in one place.
 */

import { DataLogStatus } from 'store/app/enums';
import { DateOptions } from 'store/api/enums';
import { FilterState, FilterApplyOptions } from './types';

const UNASSIGNED = 'unassigned';

const USER_NAME_FILTER_KEYS = [
  'accountCreatorName',
  'users',
  'user',
  'preparer',
  'reviewer',
  'client',
  'submittedBy',
];

const NON_FILTER_KEYS = new Set(['sort']);

/** Order in which filters are applied when multiple are active (deterministic result) */
const FILTER_APPLY_ORDER = [
  'status',
  'user',
  'nextDueDate',
  'dataLog.submissionDate',
  'organisation',
  'mappingName',
  'returnName',
  'providerName',
  'provider',
  'preparer',
  'reviewer',
  'client',
  'submittedBy',
  'period',
  'country',
  'name',
  'viesStatus',
  'accountType',
  'type',
  'accountActivated',
  'accountCreatorName',
  'users',
];

function applyOneFilter(
  data: any[] | undefined,
  key: string,
  value: any,
  options: FilterApplyOptions
): any[] | undefined {
  if (!data || data.length === 0) return data;
  const keys = key.split('.');
  const key0 = keys[0];

  if (!value || (Array.isArray(value) && value.length === 0)) {
    return data;
  }

  // accountType / type / mappingName: exclude SUBMITTED and CANCELLED before filtering
  const isReturnTypeFilter = key0 === 'accountType' || key0 === 'type';
  const isMappingNameFilter = key0 === 'mappingName';
  let currentData = data;
  if (
    (isReturnTypeFilter || isMappingNameFilter) &&
    options.excludeSubmittedCancelledForReturnType !== false
  ) {
    currentData = currentData.filter(
      (item: any) =>
        item.status !== DataLogStatus.SUBMITTED &&
        item.status !== DataLogStatus.CANCELLED
    );
  }

  // userNameFilters (array of objects) - keys[1] or fullName
  if (
    currentData.length > 0 &&
    Array.isArray(currentData[0][key0]) &&
    USER_NAME_FILTER_KEYS.includes(key0)
  ) {
    const values = Array.isArray(value) ? value : [value];
    return currentData.filter(
      (row: any) =>
        Array.isArray(row[key0]) &&
        row[key0].some((rowKey: any) => {
          const displayName =
            `${rowKey?.firstName || ''} ${rowKey?.lastName || ''}`.trim() ||
            rowKey?.email ||
            rowKey?.fullName ||
            '';
          if (keys[1]) {
            const nestedVal = rowKey?.[keys[1]];
            if (values.includes(UNASSIGNED)) {
              return nestedVal === '' || nestedVal == null;
            }
            return values.some(
              (v: string) =>
                typeof nestedVal === 'string' &&
                nestedVal.toLowerCase().includes(v.toLowerCase().trim())
            );
          }
          if (values.includes(UNASSIGNED)) {
            return !displayName;
          }
          return values.some(
            (v: string) =>
              v &&
              displayName &&
              (displayName.toLowerCase() === v.toLowerCase().trim() ||
                displayName.toLowerCase().includes(v.toLowerCase().trim()))
          );
        })
    );
  }

  // Array column (non-user)
  if (
    currentData.length > 0 &&
    Array.isArray(currentData[0][key0]) &&
    !USER_NAME_FILTER_KEYS.includes(key0)
  ) {
    return currentData.filter((row: any) =>
      row[key0].some((rowKey: any) =>
        keys[1]
          ? value === UNASSIGNED
            ? rowKey[keys[1]] === ''
            : value?.includes(rowKey[keys[1]])
          : value === UNASSIGNED
          ? rowKey === ''
          : value?.includes(rowKey)
      )
    );
  }

  // Boolean
  if (value?.includes('false') || value?.includes('true')) {
    return currentData.filter((row: any) =>
      value?.includes(row[key0]?.toString())
    );
  }

  // providerName / provider
  if (key0 === 'providerName' || key0 === 'provider') {
    return currentData.filter((row: any) => {
      if (!row.providerName) return false;
      return (Array.isArray(value) ? value : [value]).some((val: string) => {
        if (!val || typeof val !== 'string') return false;
        const valLower = val.toLowerCase().trim();
        const providerNameLower = row.providerName?.toLowerCase();
        return providerNameLower === valLower;
      });
    });
  }

  // submittedBy
  if (key0 === 'submittedBy') {
    return currentData.filter((row: any) => {
      const submittedByUser =
        row.dataLog?.submittedByUser ?? row.submittedByUser ?? null;
      const submittedByText =
        row.submittedByName ??
        row.dataLog?.submittedBy ??
        (typeof row.submittedBy?.[0] === 'string' ? row.submittedBy[0] : null);

      const check = (val: string) => {
        const valLower = val.toLowerCase().trim();
        if (submittedByUser) {
          const firstName = submittedByUser.firstName || '';
          const lastName = submittedByUser.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();
          const email = submittedByUser.email || '';
          if (
            fullName &&
            (fullName.toLowerCase() === valLower ||
              fullName.toLowerCase().includes(valLower))
          )
            return true;
          if (email && email.toLowerCase() === valLower) return true;
          if (
            firstName &&
            !lastName &&
            firstName.toLowerCase() === valLower
          )
            return true;
          if (val === 'unassigned' && !firstName) return true;
        } else if (submittedByText) {
          const v = submittedByText.trim();
          if (
            v.toLowerCase() === valLower ||
            v.toLowerCase().includes(valLower)
          )
            return true;
        } else if (val === 'unassigned') {
          return true;
        }
        return false;
      };
      return (Array.isArray(value) ? value : [value]).some(check);
    });
  }

  // returnName — requires entities in options to resolve returnTypeId → name
  if (key0 === 'returnName') {
    const { entities } = options;
    if (!entities || entities.length === 0) return currentData;
    return currentData.filter((row: any) => {
      if (!row.returnTypeId) return false;
      for (const entity of entities) {
        const returnType = entity.returnTypesEdit?.find(
          (rt) => String(rt.id) === String(row.returnTypeId)
        );
        if (returnType?.name) {
          const nameLower = returnType.name.toLowerCase().trim();
          return (Array.isArray(value) ? value : [value]).some((val: string) => {
            if (!val || typeof val !== 'string') return false;
            return nameLower === val.toLowerCase().trim();
          });
        }
      }
      return false;
    });
  }

  // mappingName — flat on return rows, or nested under entity.returns[]
  if (key0 === 'mappingName') {
    const nestedInReturns =
      currentData.length > 0 &&
      Array.isArray(currentData[0]?.returns) &&
      !currentData[0]?.mappingName;

    if (nestedInReturns) {
      return currentData.filter(
        (row: any) =>
          Array.isArray(row.returns) &&
          row.returns.some((ret: any) =>
            (Array.isArray(value) ? value : [value]).some((val: string) => {
              if (!val || typeof val !== 'string') return false;
              const valLower = val.toLowerCase().trim();
              const name = ret?.mappingName?.toLowerCase();
              return name && name.includes(valLower);
            })
          )
      );
    }

    return currentData.filter((row: any) => {
      if (!row.mappingName) return false;
      return (Array.isArray(value) ? value : [value]).some((val: string) => {
        if (!val || typeof val !== 'string') return false;
        const valLower = val.toLowerCase().trim();
        const mappingNameLower = row.mappingName?.toLowerCase();
        return mappingNameLower && mappingNameLower.includes(valLower);
      });
    });
  }

  // user (preparer OR reviewer OR client; unassigned = no user in any role)
  if (key0 === 'user') {
    const userValues = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
    if (userValues.length === 0) return currentData;
    let filtered = currentData.filter((row: any) => {
      return userValues.some((val: string) => {
        if (!val || typeof val !== 'string') return false;
        const valLower = val.toLowerCase().trim();
        // Unassigned: show returns with no user assigned to preparer, reviewer, or client.
        // Include rows with no dataLog (e.g. Not Started) or dataLog with no roles assigned.
        if (valLower === UNASSIGNED) {
          if (!row.dataLog) return true;
          const hasPreparer = !!(
            row.dataLog?.preparerUser &&
            (row.dataLog.preparerUser.firstName || row.dataLog.preparerUser.email)
          );
          const hasReviewer = !!(
            row.dataLog?.reviewerUser &&
            (row.dataLog.reviewerUser.firstName || row.dataLog.reviewerUser.email)
          );
          const hasClient = !!(
            row.dataLog?.clientUser &&
            (row.dataLog.clientUser.firstName || row.dataLog.clientUser.email)
          );
          return !hasPreparer && !hasReviewer && !hasClient;
        }
        if (row.dataLog?.preparerUser) {
          const n =
            `${row.dataLog.preparerUser.firstName || ''} ${row.dataLog.preparerUser.lastName || ''}`.trim() ||
            row.dataLog.preparerUser.email ||
            '';
          if (n && (n.toLowerCase() === valLower || n.toLowerCase().includes(valLower)))
            return true;
        }
        if (row.dataLog?.reviewerUser) {
          const n =
            `${row.dataLog.reviewerUser.firstName || ''} ${row.dataLog.reviewerUser.lastName || ''}`.trim() ||
            row.dataLog.reviewerUser.email ||
            '';
          if (n && (n.toLowerCase() === valLower || n.toLowerCase().includes(valLower)))
            return true;
        }
        if (row.dataLog?.clientUser) {
          const n =
            `${row.dataLog.clientUser.firstName || ''} ${row.dataLog.clientUser.lastName || ''}`.trim() ||
            row.dataLog.clientUser.email ||
            '';
          if (n && (n.toLowerCase() === valLower || n.toLowerCase().includes(valLower)))
            return true;
        }
        return false;
      });
    });
    if (options.excludeSubmittedCancelledForReturnType !== false) {
      filtered = filtered.filter(
        (item: any) =>
          item.status !== DataLogStatus.SUBMITTED &&
          item.status !== DataLogStatus.CANCELLED
      );
    }
    return filtered;
  }

  // nextDueDate - DateOptions.THIS_MONTH (string)
  if (
    key0 === 'nextDueDate' &&
    typeof value === 'string' &&
    value === DateOptions.THIS_MONTH
  ) {
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentMonthEnd = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );
    return currentData.filter((row: any) => {
      if (!row.nextDueDate) return false;
      const dueDate = new Date(row.nextDueDate);
      return dueDate >= currentMonthStart && dueDate <= currentMonthEnd;
    });
  }

  // dataLog.submissionDate - DateOptions.THIS_MONTH
  if (
    key === 'dataLog.submissionDate' &&
    typeof value === 'string' &&
    value === DateOptions.THIS_MONTH
  ) {
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentMonthEnd = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );
    return currentData.filter((row: any) => {
      if (!row.dataLog?.submissionDate) return false;
      const d = new Date(row.dataLog.submissionDate);
      return d >= currentMonthStart && d <= currentMonthEnd;
    });
  }

  // nextDueDate or dataLog.submissionDate - date range (array of 2)
  if (
    (key0 === 'nextDueDate' || key === 'dataLog.submissionDate') &&
    Array.isArray(value) &&
    value.length === 2
  ) {
    const fullKey = key === 'dataLog.submissionDate' ? 'dataLog.submissionDate' : key0;
    const getNested = (item: any, k: string) => {
      if (k.includes('.')) {
        let v = item;
        for (const p of k.split('.')) v = v?.[p];
        return v;
      }
      return item[k];
    };
    const [startStr, endStr] = value;
    const startMs = startStr ? new Date(startStr).getTime() : null;
    const endMs = endStr ? new Date(endStr).getTime() : null;
    return currentData.filter((row: any) => {
      const itemDate = getNested(row, fullKey);
      if (!itemDate) return false;
      const itemMs = new Date(itemDate).getTime();
      if (startMs != null && endMs != null) return itemMs >= startMs && itemMs <= endMs;
      if (startMs != null) return itemMs >= startMs;
      if (endMs != null) return itemMs <= endMs;
      return true;
    });
  }

  // period
  if (key0 === 'period') {
    // Dates may be string | Date | number — coerce before .split
    const normalize = (s: unknown) => {
      if (s == null || s === '') return '';
      if (s instanceof Date) {
        return Number.isNaN(s.getTime()) ? '' : s.toISOString().split('T')[0];
      }
      return String(s).split('T')[0].split(' ')[0];
    };
    return currentData.filter((row: any) => {
      const rowFrom =
        row.dataLog?.fromReportingDate ?? row.fromReportingDate;
      const rowTo = row.dataLog?.toReportingDate ?? row.toReportingDate;
      if (!rowFrom || !rowTo) return false;
      return (Array.isArray(value) ? value : [value]).some((val: string) => {
        if (!val || typeof val !== 'string') return false;
        const [selFrom, selTo] = val.split('_');
        if (!selFrom || !selTo) return false;
        return (
          normalize(rowFrom) === normalize(selFrom) &&
          normalize(rowTo) === normalize(selTo)
        );
      });
    });
  }

  // preparer, reviewer, client (single role)
  if (USER_NAME_FILTER_KEYS.includes(key0) && key0 !== 'user' && key0 !== 'submittedBy') {
    return currentData.filter((row: any) =>
      value === UNASSIGNED
        ? row[key0] == null || row[key0] === undefined
        : row[key0] &&
          (value?.includes(
            `${row[key0]?.['firstName'] || ''} ${row[key0]?.['lastName'] || ''}`.trim()
          ) || value?.includes(row[key0]))
    );
  }

  // status
  if (key0 === 'status') {
    let filtered = currentData.filter((row: any) => {
      const rowStatus = row[key0];
      return (Array.isArray(value) ? value : [value]).some(
        (filterStatus: string) => {
          if (
            filterStatus === 'NOT_STARTED' ||
            filterStatus === DataLogStatus.NOT_STARTED
          ) {
            return (
              !rowStatus ||
              rowStatus === '' ||
              rowStatus === DataLogStatus.NOT_STARTED
            );
          }
          const enumValue =
            DataLogStatus[filterStatus as keyof typeof DataLogStatus];
          if (rowStatus === filterStatus) return true;
          if (
            enumValue &&
            enumValue !== filterStatus &&
            rowStatus === enumValue
          )
            return true;
          return false;
        }
      );
    });
    const explicitlyIncludesSubmitted = (Array.isArray(value) ? value : [value]).some(
      (s: string) =>
        s === DataLogStatus.SUBMITTED ||
        s === 'SUBMITTED' ||
        DataLogStatus[s as keyof typeof DataLogStatus] === DataLogStatus.SUBMITTED
    );
    const explicitlyIncludesCancelled = (Array.isArray(value) ? value : [value]).some(
      (s: string) =>
        s === DataLogStatus.CANCELLED ||
        s === 'CANCELLED' ||
        DataLogStatus[s as keyof typeof DataLogStatus] === DataLogStatus.CANCELLED
    );
    if (!explicitlyIncludesSubmitted && !explicitlyIncludesCancelled) {
      filtered = filtered.filter(
        (row: any) =>
          row[key0] !== DataLogStatus.SUBMITTED &&
          row[key0] !== DataLogStatus.CANCELLED
      );
    }
    return filtered;
  }

  // Number filter: value is [number, operator] e.g. [100, '>']
  const numFilterValue =
    Array.isArray(value) &&
    value.length >= 1 &&
    typeof value[0] === 'number' &&
    (value.length === 1 || typeof value[1] === 'string');
  if (numFilterValue) {
    const numVal = value[0] as number;
    const op = (value[1] as string) || '=';
    return currentData.filter((row: any) => {
      const rowVal = row[key0];
      if (rowVal == null || rowVal === '') return false;
      const n = Number(rowVal);
      if (Number.isNaN(n)) return false;
      switch (op) {
        case '>':
          return n > numVal;
        case '<':
          return n < numVal;
        case '>=':
          return n >= numVal;
        case '<=':
          return n <= numVal;
        case '!=':
          return n !== numVal;
        case '=':
        default:
          return n === numVal;
      }
    });
  }

  // default: simple include
  let filtered = currentData.filter((row: any) =>
    value === UNASSIGNED
      ? row[key0] === '' || row[key0] == null
      : (Array.isArray(value) ? value : [value])?.includes(row[key0])
  );
  if (
    (isReturnTypeFilter || isMappingNameFilter) &&
    options.excludeSubmittedCancelledForReturnType !== false
  ) {
    filtered = filtered.filter(
      (item: any) =>
        item.status !== DataLogStatus.SUBMITTED &&
        item.status !== DataLogStatus.CANCELLED
    );
  }
  return filtered;
}

/**
 * Applies full filter state to data in a deterministic order.
 * Used by ScopeTable for client-side filtering. Does not handle date-range or net-amount filters
 * (those stay in ScopeTable as they use local state). Only key-value filters that affect the data array.
 */
export function applyFilters(
  initialData: any[] | undefined,
  filterState: FilterState,
  options: FilterApplyOptions = {}
): any[] | undefined {
  if (!initialData) return initialData;

  const keysToApply = FILTER_APPLY_ORDER.filter((k) => {
    if (NON_FILTER_KEYS.has(k)) return false;
    const v = filterState[k]?.value;
    return v && (Array.isArray(v) ? v.length > 0 : true);
  });
  Object.keys(filterState).forEach((k) => {
    if (NON_FILTER_KEYS.has(k)) return;
    if (!FILTER_APPLY_ORDER.includes(k)) {
      const v = filterState[k]?.value;
      if (v && (Array.isArray(v) ? v.length > 0 : true)) keysToApply.push(k);
    }
  });

  let data = initialData;
  for (const key of keysToApply) {
    const value = filterState[key]?.value;
    data = applyOneFilter(data, key, value, options) ?? data;
  }
  return data;
}
